import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, push } from "firebase/database";

const PageContainer = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 90%;
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  color: #00ff00;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Header = styled.h2`
  text-align: center;
  color: #00ff00;
  margin-bottom: 1.5rem;
  font-size: 2.5rem;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Input = styled.input`
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  border: 1px solid #00ff00;
  border-radius: 4px;
  background-color: transparent;
  color: #00ff00;
  flex: 1;

  &::placeholder {
    color: #00ff00;
  }
`;

const TextArea = styled.textarea`
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  border: 1px solid #00ff00;
  border-radius: 4px;
  background-color: transparent;
  color: #00ff00;

  &::placeholder {
    color: #00ff00;
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: #00ff00;
  color: #000;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    background-color: #00cc00;
  }
`;

const NameContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Contact = ({ firebase }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    handleResize();

    const columns = Math.floor(window.innerWidth / 10);
    const drops = Array(columns).fill(1);

    const draw = () => {
      context.fillStyle = 'rgba(0, 0, 0, 0.05)';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = '#00ff00';
      context.font = '10px monospace';

      drops.forEach((y, index) => {
        const text = String.fromCharCode(Math.floor(Math.random() * 128));
        const x = index * 10;
        context.fillText(text, x, y * 10);

        if (y * 10 > canvas.height && Math.random() > 0.975) {
          drops[index] = 0;
        }
        drops[index]++;
      });

      requestAnimationFrame(draw);
    };

    draw();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);

    try {
      const db = getDatabase(firebase);
      const messagesRef = ref(db, 'messages');
      await push(messagesRef, formData);
      alert('Message sent successfully!');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Error saving message to Firebase:', error);
      alert('Failed to send message. Please try again later.');
    }
  };

  const handleResumeClick = () => {
    navigate('/resume');
  };

  return (
    <PageContainer>
      <Canvas ref={canvasRef} />
      <ContactForm onSubmit={handleSubmit}>
        <Header>Contact Me</Header>
        <NameContainer>
          <Input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
          <Input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </NameContainer>
        <Input
          type="email"
          name="email"
          placeholder="Your Email"
          value={formData.email}
          onChange={handleChange}
          required
        />
        <Input
          type="tel"
          name="phone"
          placeholder="Phone Number"
          value={formData.phone}
          onChange={handleChange}
          required
        />
        <Input
          type="text"
          name="subject"
          placeholder="Subject"
          value={formData.subject}
          onChange={handleChange}
          required
        />
        <TextArea
          name="message"
          placeholder="Your Message"
          value={formData.message}
          onChange={handleChange}
          rows="4"
          required
        />
        <Button type="submit">Submit</Button>
      </ContactForm>
    </PageContainer>
  );
};

export default Contact;
